import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Brush } from "lucide-react";
import { MaskedLine } from "./Reveal";
import { scrollToId } from "@/lib/scrollTo";

const SMOKE_VIDEO = "/smoke-reveal.mp4";

export const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  useEffect(() => {
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: videoY, scale: videoScale }} className="absolute inset-0 will-change-transform">
        <video
          ref={videoRef}
          data-testid="hero-video-smoke"
          className="w-full h-full object-cover opacity-70 object-center sm:scale-[1.22] sm:translate-x-[10%] sm:-translate-y-[7%]"
          src={SMOKE_VIDEO}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09]/70 via-[#0C0A09]/35 to-[#0C0A09]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0C0A09]/80 via-transparent to-[#0C0A09]/40" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-20">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10 bg-[#41C2F2]/70" />
            <p className="text-xs uppercase tracking-[0.35em] font-mono text-[#41C2F2]">PETRAITS · Fine Art Pet Portraits</p>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-serif tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-[#F5FBFE]">
            <MaskedLine delay={0.35}>Not printed.</MaskedLine>
            <MaskedLine delay={0.5}>Not generated.</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="gold-foil-text italic">Painted by hand.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-7 text-base sm:text-lg text-stone-300 leading-relaxed max-w-xl"
          >
            The big platforms promise a portrait in 24 hours. Ours begins with a real conversation —
            in person, by phone, or by email — because a one-of-a-kind painting of your companion
            can't come off an assembly line.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-commission"
              onClick={() => scrollToId("consult")}
              className="group px-8 py-4 rounded-full bg-[#41C2F2] text-[#0C0A09] font-semibold text-sm tracking-wide hover:bg-[#BDE9FA] transition-colors duration-300"
            >
              Commission Your Portrait
            </button>
            <button
              data-testid="hero-cta-gallery"
              onClick={() => scrollToId("gallery")}
              className="px-8 py-4 rounded-full border border-stone-600 text-stone-200 text-sm tracking-wide hover:border-[#41C2F2] hover:text-[#41C2F2] transition-colors duration-300"
            >
              View Atelier Samples
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-12 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-stone-900/60 border border-stone-800 backdrop-blur-sm"
          >
            <Brush className="w-4 h-4 text-[#41C2F2]" />
            <span className="text-xs tracking-wide text-stone-400">Proprietary hand-painted technique · No algorithms · No shortcuts</span>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("method")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-stone-500 hover:text-[#41C2F2] transition-colors"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
          <ArrowDown className="w-5 h-5" />
        </motion.span>
      </motion.button>
    </section>
  );
};
